import {
  ApiError,
  Auth0AccountRole,
  ParentLimitedResponse,
  StudentLimitedResponse,
  TutorLimitedResponse,
  MessageResponse,
  MessagesService,
  MessageDirection,
} from "client/openapi";
import { LockClosedIcon } from "@radix-ui/react-icons";
import { privateNote } from "components/Dashboard/OrgCommunication/privacy";
import moment from "moment-timezone";
import { useCallback, useContext, useEffect, useState } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { APIResponse, PageStatus } from "types";
import { MessageDeliveryTag } from "components/MessageDeliveryTag";
import Notifications from "util/notifications";
import { renderTimestamp } from "util/renderTimestamp";
import "./index.css";
import { getUserTypeColor } from "util/contextColor";

function CommunicationDialog({
  student,
  tutor,
  parent,
  messages,
  setMessages,
}: {
  student?: StudentLimitedResponse;
  tutor?: TutorLimitedResponse;
  parent?: ParentLimitedResponse;
  messages: MessageResponse[];
  setMessages: React.Dispatch<React.SetStateAction<MessageResponse[]>>;
}) {
  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [error, setError] = useState<APIResponse>();

  const timezone = moment.tz.guess(true);

  const getMessages = useCallback(async () => {
    setStatus(PageStatus.LOADING);

    MessagesService.getUserCommunication({
      studentId: student?.id,
      tutorId: tutor?.id,
      parentId: parent?.id,
    })
      .then((messagesResponse) => {
        setMessages(messagesResponse);
        setStatus(PageStatus.SUCCESS);
      })
      .catch((e: ApiError) => {
        setStatus(PageStatus.ERROR);
        setError({ error: "An unexpected error occurred" });
        console.error(`Error (#${e.status}): ${e.message}`);
      });
  }, [parent?.id, setMessages, student?.id, tutor?.id]);

  useEffect(() => {
    getMessages();
  }, [getMessages]);

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  // Mail kept to some admins says who sees it.
  const { account } = useContext(OrgRolesAndAccountContext);

  return (
    <div className="account-notes--dialog">
      {status === PageStatus.LOADING ? (
        <div className="flex flex-col items-center justify-center gap-4 py-5">
          <span
            className="spinner-border"
            role="status"
            aria-hidden="true"
          ></span>

          <p>Loading...</p>
        </div>
      ) : null}

      {status === PageStatus.SUCCESS && (
        <div>
          {messages.length > 0 ? (
            messages
              .sort((a, b) => {
                const aCreated = new Date(a.created_at).getTime();
                const bCreated = new Date(b.created_at).getTime();

                if (aCreated > bCreated) {
                  // If time A is more recent than time B, put A first
                  return -1;
                } else if (aCreated < bCreated) {
                  // If time B is more recent than time A, put B first
                  return 1;
                } else {
                  // If times are the same, keep original order
                  return 0;
                }
              })
              .map((n) => {
                const authorColor =
                  n.direction === MessageDirection.INBOUND
                    ? getUserTypeColor(Auth0AccountRole.ME)
                    : getUserTypeColor(Auth0AccountRole.ORG_ADMIN);

                const senderName =
                  n.direction === MessageDirection.INBOUND
                    ? student?.first_name ||
                      tutor?.first_name ||
                      parent?.first_name
                    : n.sender?.first_name || n.sender_email;

                return (
                  <div className={`account-note ${authorColor}`} key={n.id}>
                    <div className="account-notes--meta">
                      <div>
                        <span className="block">
                          <b>{senderName}</b>
                        </span>

                        <span className="font-thin account-notes--timestamp">
                          <span
                            title={moment(n.created_at)
                              .tz(timezone)
                              .format("YYYY-MM-DD hh:mm A")}
                          >
                            {renderTimestamp(
                              moment(n.created_at).tz(timezone).toDate()
                            )}
                          </span>
                        </span>
                      </div>

                      <div>
                        <MessageDeliveryTag message={n} />
                      </div>
                    </div>
                    {privateNote(n, account?.reference_id) && (
                      <p className="flex items-center gap-1 text-xs text-gray-600">
                        <LockClosedIcon aria-hidden />
                        {privateNote(n, account?.reference_id)}
                      </p>
                    )}
                    <p className="account-notes--note">{n.content}</p>
                  </div>
                );
              })
          ) : (
            <p>No messages.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default CommunicationDialog;
